import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPlans, deletePlan } from '../../api/plans';
import { updateAdminPlanStatus } from '../../api/admin';
import PaginationControls from '../../components/common/PaginationControls';
import AdminSidebar from '../../components/admin/AdminSidebar';
import NoticeToast from '../../components/common/NoticeToast';
import { useNotice } from '../../hooks/useNotice';
import './PlansManagement.css';

const ITEMS_PER_PAGE = 30;

const normalizePlanStatus = (status) => {
  const value = String(status || '').toLowerCase().trim();
  if (!value || value === 'non_analyse' || value === 'non analyse' || value === 'non-analyse' || value === 'brouillon') {
    return 'analyse';
  }
  if (value === 'rejeté' || value === 'rejete') {
    return 'non approuvé';
  }
  return value;
};

const STATUS_OPTIONS = [
  { value: 'analyse', label: 'En analyse' },
  { value: 'validé', label: 'Validé' },
  { value: 'non validé', label: 'Non validé' },
  { value: 'approuvé', label: 'Approuvé' },
  { value: 'non approuvé', label: 'Refusé' },
];

const PlansManagement = () => {
  const navigate = useNavigate();
  const { notice, showNotice, clearNotice } = useNotice();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChef, setFilterChef] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterProgramme, setFilterProgramme] = useState('');
  const [filterModeEnseignement, setFilterModeEnseignement] = useState('');
  const [filterCampus, setFilterCampus] = useState('');
  const [chefs, setChefs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [modesEnseignement, setModesEnseignement] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date_upload');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await getAllPlans();
      const normalized = data.map((plan) => ({
        ...plan,
        statut: normalizePlanStatus(plan.statut),
      }));
      setPlans(normalized);
      
      // Extract unique values for filters
      const uniqueChefs = [...new Set(normalized.map(p => p.chef_equipe_nom).filter(Boolean))];
      const uniqueSessions = [...new Set(normalized.map(p => p.session).filter(Boolean))];
      const uniqueProgrammes = [...new Set(normalized.map(p => p.programme).filter(Boolean))];
      const uniqueModes = [...new Set(normalized.map(p => p.mode_enseignement).filter(Boolean))];
      const uniqueCampuses = [...new Set(normalized.map(p => p.campus).filter(Boolean))];
      
      setChefs(uniqueChefs);
      setSessions(uniqueSessions);
      setProgrammes(uniqueProgrammes);
      setModesEnseignement(uniqueModes);
      setCampuses(uniqueCampuses);
    } catch (error) {
      console.error('Erreur lors du chargement des plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    
    try {
      await deletePlan(planToDelete.id);
      setPlans(plans.filter(p => p.id !== planToDelete.id));
      setShowDeleteModal(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showNotice('Erreur lors de la suppression du plan.', 'error');
    }
  };

  const handleStatusChange = async (planId, newStatus) => {
    try {
      const normalizedStatus = normalizePlanStatus(newStatus);
      const updatedPlan = await updateAdminPlanStatus(planId, normalizedStatus);
      setPlans(plans.map(p => 
        p.id === planId ? { ...p, statut: normalizePlanStatus(updatedPlan.statut) } : p
      ));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      showNotice(error.message || 'Erreur lors de la mise a jour du statut.', 'error');
    }
  };

  const handleSortChange = (column, direction) => {
    if (!direction) {
      setSortBy('date_upload');
      setSortDirection('desc');
      return;
    }
    setSortBy(column);
    setSortDirection(direction);
  };

  const getSortIndicator = (column) => {
    if (sortBy !== column) {
      return '↕';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const filteredPlans = useMemo(() => {
    const filtered = plans.filter(plan => {
    const matchSearch = 
      plan.cours?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.enseignant_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.chef_equipe_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.numero_cours?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = !filterStatus || normalizePlanStatus(plan.statut) === filterStatus;
    const matchChef = !filterChef || plan.chef_equipe_nom === filterChef;
    const matchSession = !filterSession || plan.session === filterSession;
    const matchProgramme = !filterProgramme || plan.programme === filterProgramme;
    const matchModeEnseignement = !filterModeEnseignement || plan.mode_enseignement === filterModeEnseignement;
    const matchCampus = !filterCampus || plan.campus === filterCampus;
    
    return matchSearch && matchStatus && matchChef && matchSession && 
          matchProgramme && matchModeEnseignement && matchCampus;
    });

    const statusRank = {
      analyse: 0,
      'non validé': 1,
      validé: 2,
      approuvé: 3,
      'non approuvé': 4,
    };

    const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), 'fr', { sensitivity: 'base' });

    return filtered.sort((a, b) => {
      const directionFactor = sortDirection === 'asc' ? 1 : -1;

      if (sortBy === 'date_upload') {
        const dateA = new Date(a.updated_at || a.date_upload || 0).getTime();
        const dateB = new Date(b.updated_at || b.date_upload || 0).getTime();
        if (dateA !== dateB) {
          return (dateA - dateB) * directionFactor;
        }
      } else if (sortBy === 'statut') {
        const rankA = statusRank[normalizePlanStatus(a.statut)] ?? 999;
        const rankB = statusRank[normalizePlanStatus(b.statut)] ?? 999;
        if (rankA !== rankB) {
          return (rankA - rankB) * directionFactor;
        }
      } else {
        const textCompare = compareText(a[sortBy], b[sortBy]);
        if (textCompare !== 0) {
          return textCompare * directionFactor;
        }
      }
      return b.id - a.id;
    });
  }, [plans, searchTerm, filterStatus, filterChef, filterSession, filterProgramme, filterModeEnseignement, filterCampus, sortBy, sortDirection]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredPlans.length / ITEMS_PER_PAGE));
  }, [filteredPlans.length]);

  const planKpis = useMemo(() => {
    const approvedCount = plans.filter((plan) => normalizePlanStatus(plan.statut) === 'approuvé').length;
    const reviewCount = plans.filter((plan) => ['analyse', 'validé'].includes(normalizePlanStatus(plan.statut))).length;
    const rejectedCount = plans.filter((plan) => ['non validé', 'non approuvé'].includes(normalizePlanStatus(plan.statut))).length;

    return [
      { label: 'Total plans', value: plans.length, helper: 'tous statuts' },
      { label: 'En suivi', value: reviewCount, helper: 'brouillon à validé' },
      { label: 'Approuvés', value: approvedCount, helper: 'finalisés' },
      { label: 'Résultats filtrés', value: filteredPlans.length, helper: 'vue actuelle' },
      { label: 'À corriger', value: rejectedCount, helper: 'retours ou refus' },
    ];
  }, [plans, filteredPlans.length]);

  const paginatedPlans = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPlans.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPlans, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterChef, filterSession, filterProgramme, filterModeEnseignement, filterCampus]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getStatusColor = (status) => {
    const normalized = normalizePlanStatus(status);
    const colors = {
      'analyse': '#3498db',
      'validé': '#2ecc71',
      'non validé': '#e74c3c',
      'approuvé': '#27ae60',
      'non approuvé': '#c0392b'
    };
    return colors[normalized] || '#7f8c8d';
  };

  if (loading) {
    return (
      <div className="admin-shell">
        <AdminSidebar />
        <div className="admin-content">
          <div className="plans-management">
            <div className="loading-spinner">Chargement des plans...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-content">
        <div className="plans-management">
      {/* Header */}
      <div className="plans-header">
        <div>
          <h1>Gestion des plans</h1>
          <p className="plans-count">{filteredPlans.length} plan(s) trouvé(s)</p>
        </div>
      </div>

      <div className="plans-kpi-strip">
        {planKpis.map((kpi) => (
          <div key={kpi.label} className="plans-kpi-card">
            <span className="plans-kpi-label">{kpi.label}</span>
            <strong className="plans-kpi-value">{kpi.value}</strong>
            <span className="plans-kpi-helper">{kpi.helper}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Rechercher par cours, enseignant, chef ou numéro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les statuts</option>
          {STATUS_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select 
          value={filterChef} 
          onChange={(e) => setFilterChef(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les chefs</option>
          {chefs.map(chef => (
            <option key={chef} value={chef}>{chef}</option>
          ))}
        </select>

        <select 
          value={filterSession} 
          onChange={(e) => setFilterSession(e.target.value)}
          className="filter-select"
        >
          <option value="">Toutes les sessions</option>
          {sessions.map(session => (
            <option key={session} value={session}>{session}</option>
          ))}
        </select>

        <select 
          value={filterProgramme} 
          onChange={(e) => setFilterProgramme(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les programmes</option>
          {programmes.map(prog => (
            <option key={prog} value={prog}>{prog}</option>
          ))}
        </select>

        <select 
          value={filterModeEnseignement} 
          onChange={(e) => setFilterModeEnseignement(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les modes</option>
          {modesEnseignement.map(mode => (
            <option key={mode} value={mode}>{mode}</option>
          ))}
        </select>

        <select 
          value={filterCampus} 
          onChange={(e) => setFilterCampus(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les campus</option>
          {campuses.map(campus => (
            <option key={campus} value={campus}>{campus}</option>
          ))}
        </select>

        <button 
          className="btn-reset"
          onClick={() => {
            setSearchTerm('');
            setFilterStatus('');
            setFilterChef('');
            setFilterSession('');
            setFilterProgramme('');
            setFilterModeEnseignement('');
            setFilterCampus('');
          }}
        >
          <i className="fas fa-times"></i> Réinitialiser
        </button>
      </div>

      {/* Plans Table */}
      <div className="plans-table-container">
        <table className="plans-table">
          <thead>
            <tr>
              <th>ID</th>
              <th><span className="sort-label">Cours <span className="sort-indicator" aria-hidden="true">{getSortIndicator('cours')}</span></span></th>
              <th><span className="sort-label">Numéro <span className="sort-indicator" aria-hidden="true">{getSortIndicator('numero_cours')}</span></span></th>
              <th><span className="sort-label">Session <span className="sort-indicator" aria-hidden="true">{getSortIndicator('session')}</span></span></th>
              <th><span className="sort-label">Enseignant <span className="sort-indicator" aria-hidden="true">{getSortIndicator('enseignant_nom')}</span></span></th>
              <th><span className="sort-label">Chef <span className="sort-indicator" aria-hidden="true">{getSortIndicator('chef_equipe_nom')}</span></span></th>
              <th><span className="sort-label">Programme <span className="sort-indicator" aria-hidden="true">{getSortIndicator('programme')}</span></span></th>
              <th><span className="sort-label">Mode <span className="sort-indicator" aria-hidden="true">{getSortIndicator('mode_enseignement')}</span></span></th>
              <th><span className="sort-label">Campus <span className="sort-indicator" aria-hidden="true">{getSortIndicator('campus')}</span></span></th>
              <th><span className="sort-label">Statut <span className="sort-indicator" aria-hidden="true">{getSortIndicator('statut')}</span></span></th>
              <th><span className="sort-label">Date <span className="sort-indicator" aria-hidden="true">{getSortIndicator('date_upload')}</span></span></th>
              <th>Actions</th>
            </tr>
            <tr className="sort-row">
              <th></th>
              <th>
                <select value={sortBy === 'cours' ? sortDirection : ''} onChange={(e) => handleSortChange('cours', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'numero_cours' ? sortDirection : ''} onChange={(e) => handleSortChange('numero_cours', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'session' ? sortDirection : ''} onChange={(e) => handleSortChange('session', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'enseignant_nom' ? sortDirection : ''} onChange={(e) => handleSortChange('enseignant_nom', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'chef_equipe_nom' ? sortDirection : ''} onChange={(e) => handleSortChange('chef_equipe_nom', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'programme' ? sortDirection : ''} onChange={(e) => handleSortChange('programme', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'mode_enseignement' ? sortDirection : ''} onChange={(e) => handleSortChange('mode_enseignement', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'campus' ? sortDirection : ''} onChange={(e) => handleSortChange('campus', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'statut' ? sortDirection : ''} onChange={(e) => handleSortChange('statut', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="asc">Priorité haute</option>
                  <option value="desc">Priorité basse</option>
                </select>
              </th>
              <th>
                <select value={sortBy === 'date_upload' ? sortDirection : ''} onChange={(e) => handleSortChange('date_upload', e.target.value)}>
                  <option value="">Tri</option>
                  <option value="desc">Plus récent</option>
                  <option value="asc">Plus ancien</option>
                </select>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredPlans.length > 0 ? (
              paginatedPlans.map(plan => (
                <tr key={plan.id}>
                  <td>{plan.id}</td>
                  <td className="plan-cours">{plan.cours}</td>
                  <td>{plan.numero_cours || '-'}</td>
                  <td>{plan.session || '-'}</td>
                  <td>{plan.enseignant_nom || 'N/A'}</td>
                  <td>{plan.chef_equipe_nom || 'Non assigné'}</td>
                  <td>{plan.programme || '-'}</td>
                  <td>{plan.mode_enseignement || '-'}</td>
                  <td>{plan.campus || '-'}</td>
                  <td>
                    <select
                      value={normalizePlanStatus(plan.statut)}
                      onChange={(e) => handleStatusChange(plan.id, e.target.value)}
                      className="status-select"
                      style={{ 
                        backgroundColor: getStatusColor(plan.statut),
                        color: 'white'
                      }}
                    >
                      {STATUS_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td>{new Date(plan.date_upload).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => navigate(`/admin/plan-detail/${plan.id}`)}
                        title="Voir les détails"
                        aria-label={`Voir les détails du plan ${plan.cours || plan.id}`}
                      >
                        <i className="fas fa-eye"></i>
                        <span>Voir détails</span>
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteClick(plan)}
                        title="Supprimer"
                        aria-label={`Supprimer le plan ${plan.cours || plan.id}`}
                      >
                        <i className="fas fa-trash"></i>
                        <span>Supprimer</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="no-data">
                  Aucun plan trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Confirmer la suppression</h2>
            <p>
              Êtes-vous sûr de vouloir supprimer le plan 
              <strong> "{planToDelete?.cours}"</strong> ?
            </p>
            <p className="warning-text">
              Cette action est irréversible et supprimera également toutes les validations associées.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn-confirm-delete"
                onClick={handleConfirmDelete}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
      <NoticeToast notice={notice} onClose={clearNotice} />
        </div>
      </div>
    </div>
  );
};

export default PlansManagement;
